import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { ErrorState } from '@/components/ui/ErrorState';
import { Spinner } from '@/components/ui/Spinner';
import { dashboardKeys, fetchSourcesSummary } from '@/features/dashboard/api';
import { formatNumber } from '@/features/dashboard/widgets';
import { useAssetSearch } from '@/features/catalog/hooks';
import type { DatasetStatus } from '@/features/connectors/types';
import type { ApiError } from '@/lib/errors';

const STATUS_STYLE: Record<DatasetStatus, { label: string; cls: string }> = {
  ready: { label: 'Healthy', cls: 'bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-300' },
  stale: { label: 'Needs refresh', cls: 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300' },
  error: { label: 'Error', cls: 'bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300' },
};

function timeAgo(iso: string | null): string {
  if (!iso) return 'never';
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function CatalogSearchPage(): JSX.Element {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const search = useAssetSearch(query);
  const inventory = useQuery({ queryKey: dashboardKeys.sources, queryFn: fetchSourcesSummary });

  const connectorName = useMemo(() => {
    const map = new Map<string, string>();
    (inventory.data?.connectors ?? []).forEach((c) => map.set(c.id, c.name));
    return map;
  }, [inventory.data]);

  const datasets = useMemo(() => inventory.data?.datasets ?? [], [inventory.data]);
  const totalRecords = datasets.reduce((s, d) => s + (d.row_count ?? 0), 0);
  const totalFields = datasets.reduce((s, d) => s + (d.column_count ?? 0), 0);

  const filtered = useMemo(() => {
    const q = input.trim().toLowerCase();
    if (q === '') return datasets;
    return datasets.filter(
      (d) =>
        d.name.toLowerCase().includes(q) ||
        (connectorName.get(d.connector_id) ?? '').toLowerCase().includes(q),
    );
  }, [datasets, input, connectorName]);

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    setQuery(input.trim());
  }

  return (
    <div className="space-y-5">
      {/* Summary line */}
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm text-ink-500 dark:text-ink-400">
        <span className="font-display text-base font-semibold text-ink-900 dark:text-ink-50">
          {formatNumber(datasets.length)} dataset{datasets.length === 1 ? '' : 's'}
        </span>
        <span>· {formatNumber(totalRecords)} total records</span>
        <span>· {formatNumber(totalFields)} fields</span>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-wrap gap-2">
        <div className="relative max-w-xl flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400 dark:text-ink-500" />
          <input
            type="search"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Filter datasets, or press Search for tables & columns…"
            className="h-10 w-full rounded-lg border border-ink-200 dark:border-white/10 bg-white dark:bg-ink-900 pl-10 pr-3 text-sm text-ink-900 dark:text-ink-50 placeholder:text-ink-400 shadow-soft transition focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        </div>
        <button type="submit" className="btn-primary h-10 px-4 text-sm">
          Search
        </button>
      </form>

      {/* Deep (server) search results take over when a query is submitted */}
      {query !== '' ? (
        <>
          {search.isPending && <Spinner label="Searching…" />}
          {search.isError && <ErrorState error={search.error as ApiError} />}
          {search.data && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-mono text-[10px] tracking-[0.18em] text-ink-500 dark:text-ink-400">
                  {search.data.total} RESULT(S)
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setQuery('');
                    setInput('');
                  }}
                  className="font-mono text-[10px] tracking-wider text-brand-700 hover:text-brand-800 dark:text-brand-300"
                >
                  ← BACK TO ALL DATASETS
                </button>
              </div>
              {search.data.hits.map((hit, index) => {
                const fqn = hit.fullyQualifiedName ?? '';
                return (
                  <Link
                    key={fqn || index}
                    to={fqn ? `/catalog/asset?fqn=${encodeURIComponent(fqn)}` : '#'}
                    className="group block rounded-xl border border-ink-200/70 dark:border-white/10 bg-white dark:bg-ink-900 p-4 shadow-soft transition hover:-translate-y-px hover:border-ink-300 hover:shadow-elevated"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="truncate font-display text-sm font-semibold text-ink-900 dark:text-ink-50">
                          {hit.name ?? fqn}
                        </p>
                        <p className="mt-0.5 truncate font-mono text-[11px] text-ink-500 dark:text-ink-400">{fqn}</p>
                        {hit.description && (
                          <p className="mt-2 line-clamp-2 text-sm text-ink-600 dark:text-ink-300">{hit.description}</p>
                        )}
                      </div>
                      {fqn && (
                        <span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-brand-700 dark:text-brand-300">
                          View
                          <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
                        </span>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </>
      ) : inventory.isPending ? (
        <Spinner label="Loading datasets…" />
      ) : inventory.isError ? (
        <ErrorState error={inventory.error as ApiError} />
      ) : datasets.length === 0 ? (
        <div className="rounded-xl border border-dashed border-ink-300 dark:border-white/15 bg-white/60 dark:bg-white/5 p-10 text-center">
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 dark:bg-brand-900/30 text-brand-600">
            <Search className="h-4 w-4" />
          </div>
          <p className="mt-3 text-sm text-ink-600 dark:text-ink-300">No datasets catalogued yet.</p>
          <p className="mt-1 text-xs text-ink-400 dark:text-ink-500">
            Add a data source or upload a file to get started.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-ink-200/70 dark:border-white/10 bg-white dark:bg-ink-900 shadow-soft">
          <table className="w-full min-w-[680px] text-sm">
            <thead>
              <tr className="border-b border-ink-100 text-left font-mono text-[10px] uppercase tracking-[0.12em] text-ink-400 dark:border-white/10 dark:text-ink-500">
                <th className="px-5 py-3 font-medium">Dataset</th>
                <th className="px-5 py-3 font-medium">Source</th>
                <th className="px-5 py-3 text-right font-medium">Records</th>
                <th className="px-5 py-3 text-right font-medium">Fields</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Last sync</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100 dark:divide-white/5">
              {filtered.map((d) => {
                const status = STATUS_STYLE[d.status];
                return (
                  <tr key={d.id} className="group transition hover:bg-ink-50/60 dark:hover:bg-white/[0.03]">
                    <td className="px-5 py-3">
                      <Link to={`/datasets/${d.id}`} className="block">
                        <span className="font-medium text-ink-900 group-hover:text-brand-700 dark:text-ink-50 dark:group-hover:text-brand-300">
                          {d.name}
                        </span>
                        {d.om_fqn && (
                          <span className="block truncate font-mono text-[11px] text-ink-400 dark:text-ink-500">
                            {d.om_fqn}
                          </span>
                        )}
                      </Link>
                    </td>
                    <td className="px-5 py-3 text-ink-600 dark:text-ink-300">
                      {connectorName.get(d.connector_id) ?? '—'}
                    </td>
                    <td className="px-5 py-3 text-right font-mono text-xs text-ink-700 dark:text-ink-200">
                      {d.row_count === null ? '—' : formatNumber(d.row_count)}
                    </td>
                    <td className="px-5 py-3 text-right font-mono text-xs text-ink-600 dark:text-ink-300">
                      {d.column_count}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${status.cls}`}
                      >
                        {status.label}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs text-ink-500 dark:text-ink-400">{timeAgo(d.last_synced_at)}</td>
                    <td className="px-5 py-3 text-right">
                      <Link
                        to={`/datasets/${d.id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 opacity-0 transition group-hover:opacity-100 dark:text-brand-300"
                      >
                        View
                        <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <p className="px-5 py-8 text-center text-sm text-ink-400 dark:text-ink-500">
              No datasets match “{input}”.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
